<template>
  <div>
    <app-cells gap="30px">
      <h1 class="title">
        Work Schedule <span>| Sales Managers</span>
      </h1>
      <app-date-picker
        v-model="pickerModel"
        :clearable="false"
        type="week"
        placeholder="Select week"
        class="datepicker--300"
        :editable="false"
        :value-type="'date'"
        :formatter="weekInputFormatter"
      />
      <app-cells :indent="false" gap="10px">
        <app-switcher v-model="viewAll" /> View all managers
      </app-cells>
    </app-cells>
    <TheSalesWorkSchedule
      v-for="day in weekRange.days"
      :key="`ws-${day}`"
      :date="day"
      :date-label="formatDateLabel(day)"
      :week="weekRange"
      :people="employees"
      :bits-by-user="bitsByDateUser[day] || {}"
      :day-off-by-user="dayOffByDateUser[day] || {}"
      :leaves-by-user="leavesByDateUser[day] || {}"
      :hourly-summary="hourlySummaryByDate[day] || []"
      :row-versions="rowVersionByDateUser[day] || {}"
      :loading="isLoading"
      :readonly="scheduleReadonly"
      :my-user-id="user_data.id"
      @change="onScheduleChange"
      @dayoff-change="onDayOffChange"
    />
  </div>
</template>

<script>
import TheSalesWorkSchedule from '@/views/employees/sales-work-schedule/TheSalesWorkSchedule.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import { getAccessToken } from 'axios-jwt'
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns'
import {
  getEmployeesWorkScheduleUsers,
  getEmployeesWorkScheduleWorkDays,
  getEmployeesWorkScheduleDayOff,
  getEmployeesWorkScheduleUserLeaves,
  getEmployeesWorkScheduleHourlySummary,
  postEmployeesWorkScheduleWorkDays,
  postEmployeesWorkScheduleDayOff
} from '@/http'

export default {
  name: 'SalesWorkSchedule',
  components: { TheSalesWorkSchedule, AppDatePicker },

  data() {
    const today = new Date()
    const s = startOfWeek(today, { weekStartsOn: 1 })
    const e = endOfWeek(today, { weekStartsOn: 1 })
    return {
      weekAnchor: { from_date: format(s, 'yyyy-MM-dd'), to_date: format(e, 'yyyy-MM-dd') },
      employees: [],
      bitsByDateUser: {},
      dayOffByDateUser: {},
      leavesByDateUser: {},
      hourlySummaryByDate: {},
      isLoading: true,
      viewAll: false,
      ws: null,
      wsStatus: 'closed',
      wsRetry: { attempt: 0, timer: null },
      rowVersionByDateUser: {},
    }
  },

  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(g => g.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(g => g.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(g => g.code).includes('sales_supervisor')
    },
    user_data() {
      return this.$store.state.userData
    },
    apiCategory() {
      return this.viewAll ? 'all' : (this.is_sales_manager || this.is_sales_supervisor ? 'own' : 'all')
    },
    scheduleReadonly() {
      return !this.is_sales_manager && !this.is_sales_supervisor
    },
    pickerModel: {
      get() {
        return parseISO(this.weekAnchor.from_date)
      },
      set(date) {
        const s = startOfWeek(date, { weekStartsOn: 1 })
        const e = endOfWeek(date, { weekStartsOn: 1 })
        this.weekAnchor = { from_date: format(s, 'yyyy-MM-dd'), to_date: format(e, 'yyyy-MM-dd') }
      }
    },
    weekInputFormatter() {
      return {
        stringify: date => {
          const s = startOfWeek(date, { weekStartsOn: 1 })
          const e = endOfWeek(date, { weekStartsOn: 1 })
          return `${ format(s, 'dd LLL yyyy') } - ${ format(e, 'dd LLL yyyy') }`
        },
        parse: () => this.pickerModel
      }
    },
    weekRange() {
      const start = parseISO(this.weekAnchor.from_date)
      const end = parseISO(this.weekAnchor.to_date)
      const days = eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'))
      return { start: this.weekAnchor.from_date, end: this.weekAnchor.to_date, days }
    }
  },
  watch: {
    viewAll(enabled) {
      this.stopWS()
      this.fetchAll()
      if (enabled) this.startWS()
    },
    weekAnchor: {
      handler() {
        this.stopWS()
        this.fetchAll()
        if (this.viewAll) this.startWS()
      },
      deep: true
    }
  },
  created() {
    this.fetchAll()
  },
  mounted() {
    if (this.viewAll) this.startWS()
  },
  beforeDestroy() {
    this.stopWS()
  },
  methods: {
    formatDateLabel(iso) {
      return format(parseISO(iso), 'EEEE, dd LLL')
    },
    async fetchAll() {
      this.isLoading = true
      try {
        const params = {
          category: this.apiCategory,
          from_date: this.weekAnchor.from_date,
          to_date: this.weekAnchor.to_date
        }
        const [usersRes, daysRes, dayoffRes, leavesRes, hourlyRes] = await Promise.all([
          getEmployeesWorkScheduleUsers(this.apiCategory),
          getEmployeesWorkScheduleWorkDays(params),
          getEmployeesWorkScheduleDayOff(params),
          getEmployeesWorkScheduleUserLeaves(params),
          getEmployeesWorkScheduleHourlySummary({
            from_date: this.weekAnchor.from_date,
            to_date: this.weekAnchor.to_date
          })
        ])
        this.employees = Array.isArray(usersRes.data) ? usersRes.data : []
        this.bitsByDateUser = this.buildBitsByDateUser(daysRes?.data?.items || [])
        this.dayOffByDateUser = this.buildDayOffByDateUser(dayoffRes?.data?.items || [])
        this.leavesByDateUser = this.buildLeavesByDateUser(leavesRes?.data?.items || [])
        this.hourlySummaryByDate = this.buildHourlySummaryByDate(hourlyRes?.data?.items || [])
        const versions = {}
        for (const d of this.weekRange.days) {
          versions[d] = {}
        }
        this.rowVersionByDateUser = versions
      } catch (_) {
        this.employees = []
        this.bitsByDateUser = {}
        this.dayOffByDateUser = {}
        this.leavesByDateUser = {}
        this.hourlySummaryByDate = {}
        this.rowVersionByDateUser = {}
      } finally {
        this.isLoading = false
      }
    },
    async fetchHourlySummary() {
      try {
        const { data } = await getEmployeesWorkScheduleHourlySummary({
          from_date: this.weekAnchor.from_date,
          to_date: this.weekAnchor.to_date
        })
        this.hourlySummaryByDate = this.buildHourlySummaryByDate(data?.items || [])
      } catch (_) {}
    },
    buildBitsByDateUser(items) {
      const map = {}

      for (const it of items) {
        const bits = String(it.hours || '')

        if (!map[it.date]) map[it.date] = {}
        map[it.date][it.user_id] = bits
      }
      return map
    },
    buildDayOffByDateUser(items) {
      const map = {}

      for (const it of items) {
        if (!it?.date || typeof it.user_id === 'undefined') continue
        if (!map[it.date]) map[it.date] = {}
        map[it.date][it.user_id] = true
      }
      for (const d of this.weekRange.days) {
        if (!map[d]) map[d] = {}
      }
      return map
    },
    buildLeavesByDateUser(items) {
      const map = {}

      for (const it of items) {
        if (!it?.date || typeof it.user_id === 'undefined') continue
        if (!map[it.date]) map[it.date] = {}
        map[it.date][it.user_id] = { type: it.type, to_date: it.to_date }
      }
      for (const d of this.weekRange.days) {
        if (!map[d]) map[d] = {}
      }
      return map
    },
    buildHourlySummaryByDate(items) {
      const map = {}

      for (const it of items) {
        if (!it?.date || !Array.isArray(it.users) || it.users.length !== 24) continue
        map[it.date] = it.users.map(n => Number(n) || 0)
      }
      for (const d of this.weekRange.days) {
        if (!map[d]) map[d] = new Array(24).fill(0)
      }
      return map
    },
    async onScheduleChange({ personId, bits, date }) {
      if (!this.user_data || personId !== this.user_data.id || this.scheduleReadonly) return
      try {
        const { data } = await postEmployeesWorkScheduleWorkDays({ date, hours: bits })
        const dayMap = { ...(this.bitsByDateUser[date] || {}) }
        dayMap[data.user_id] = data.hours
        this.$set(this.bitsByDateUser, date, dayMap)

        if (!this.viewAll) await this.fetchHourlySummary()
      } catch (_) {}
    },
    async onDayOffChange({ personId, dayOff, date }) {
      if (!this.user_data || personId !== this.user_data.id || this.scheduleReadonly) return
      try {
        await postEmployeesWorkScheduleDayOff({ action: dayOff ? 'create' : 'delete', date })
        const cur = { ...(this.dayOffByDateUser[date] || {}) }
        if (dayOff) cur[personId] = true
        else delete cur[personId]
        this.$set(this.dayOffByDateUser, date, cur)
      } catch (_) {}
    },
    decodeJwtExp(t) {
      try {
        return JSON.parse(atob(t.split('.')[1])).exp || null
      } catch (_) { return null }
    },

    startWS() {
      const raw = getAccessToken()
      const token = raw ? raw.replace(/^Bearer\s+/i, '') : ''
      const exp = this.decodeJwtExp(token)
      const now = Math.floor(Date.now() / 1000)
      const ttl = exp ? (exp - now) : null
      if (!token || (ttl !== null && ttl <= 0)) return

      const { from_date, to_date } = this.weekAnchor
      const qs = new URLSearchParams({ token, from_date, to_date }).toString()
      const url = `wss://dev-ws.altezza.travel/ws/employees/sales/work-schedule/?${ qs }`

      if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
        try { this.ws.close(4000, 'reopen') } catch (_) {}
      }

      let ws = null
      try {
        ws = new WebSocket(url)
      } catch (_) {
        this.scheduleReconnect()
        return
      }

      this.ws = ws
      this.wsStatus = 'connecting'

      ws.onopen = () => {
        this.wsStatus = 'open'
        this.wsRetry.attempt = 0
      }

      ws.onmessage = (ev) => {
        if (typeof ev.data === 'string' && ev.data.toLowerCase() === 'ping') {
          try {
            ws.send('pong')
          } catch (_) {}
          return
        }
        let msg = null
        try { msg = JSON.parse(ev.data) } catch (_) { return }
        if (msg && msg.type === 'work-schedule') this.handleWsEvent(msg)
      }

      ws.onclose = () => {
        this.wsStatus = 'closed'
        this.scheduleReconnect()
      }

      ws.onerror = () => {
      }
    },
    stopWS() {
      if (this.wsRetry.timer) {
        clearTimeout(this.wsRetry.timer)
        this.wsRetry.timer = null
      }
      if (this.ws) {
        try { this.ws.close(4001, 'stopWS') } catch (_) {}
        this.ws = null
      }
      this.wsStatus = 'closed'
    },
    scheduleReconnect() {
      if (!this.viewAll) return

      const maxDelay = 15000
      const base = 1000 * Math.pow(2, Math.min(this.wsRetry.attempt, 4))
      const jitter = Math.random() * 500
      const delay = Math.min(base + jitter, maxDelay)
      this.wsRetry.attempt += 1
      this.wsRetry.timer = setTimeout(() => this.startWS(), delay)
    },
    handleWsEvent(msg) {
      const ev = msg.event
      const data = msg.data || {}

      if (ev === 'day-off.changed') {
        const cur = { ...(this.dayOffByDateUser[data.date] || {}) }
        if (data.action === 'create') cur[data.user] = true
        else delete cur[data.user]
        this.$set(this.dayOffByDateUser, data.date, cur)
        return
      }

      if (ev === 'user-leave.changed') {
        const cur = { ...(this.leavesByDateUser[data.date] || {}) }
        if (data.action === 'create') {
          cur[data.user_id] = { type: data.type, to_date: data.to_date }
        } else {
          delete cur[data.user_id]
        }
        this.$set(this.leavesByDateUser, data.date, cur)
        return
      }

      if (ev === 'hourly-summary.changed') {
        const arr = Array.isArray(data.users) ? data.users.map(n => Number(n) || 0) : new Array(24).fill(0)
        this.$set(this.hourlySummaryByDate, data.date, arr)
        return
      }

      if (ev === 'work-day.updated') {
        const hours = (data.hours || '').replace(/[^01]/g, '').padEnd(48, '0').slice(0, 48)
        const dayMap = { ...(this.bitsByDateUser[data.date] || {}) }
        dayMap[data.user_id] = hours
        this.$set(this.bitsByDateUser, data.date, dayMap)

        const byDate = { ...(this.rowVersionByDateUser[data.date] || {}) }
        byDate[data.user_id] = (byDate[data.user_id] || 0) + 1
        this.$set(this.rowVersionByDateUser, data.date, byDate)
      }
    }
  }
}
</script>
